import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isRestrictedVendor } from '../utils/permissions';
import { facilityService, vendorService } from '../services/dataService';
import styles from './assets/css/FacilitiesPage.module.css';

const initialForm = {
  id: '',
  name: '',
  keph_level: 3,
  ownership: 'public',
  county: '',
  subcounty: '',
  ward: '',
  hmis: '',
  source: 'manual_entry',
  callback_url: 'http://localhost:8000/requests/callback',
  username: '',
  password: '',
  status: 'active',
  sha_contract_status: 'pending',
  fr_data: null,
  has_nesp_contract: false,
};

const initialEditForm = {
  id: '',
  name: '',
  keph_level: 3,
  ownership: 'public',
  county: '',
  subcounty: '',
  ward: '',
  hmis: '',
  source: 'manual_entry',
  callback_url: 'http://localhost:8000/requests/callback',
  username: '',
  password: '',
  status: 'active',
  sha_contract_status: 'pending',
};

const FacilitiesPage = () => {
  const { user } = useAuth();
  const isVendorUser = isRestrictedVendor(user);
  const [form, setForm] = useState(initialForm);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [manualEntryEnabled, setManualEntryEnabled] = useState(false);
  const [lookupMessage, setLookupMessage] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupSucceeded, setLookupSucceeded] = useState(false);

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [countyFilter, setCountyFilter] = useState('');
  const [hmisFilter, setHmisFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [hmisOptions, setHmisOptions] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState(initialEditForm);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const getFacilityFilters = useCallback(() => ({
    county: countyFilter || undefined,
    hmis: hmisFilter || undefined,
    status: statusFilter || undefined,
    search: nameFilter || undefined,
  }), [countyFilter, hmisFilter, statusFilter, nameFilter]);

  const loadFacilities = useCallback(async (targetPage = page) => {
    setLoadError('');
    setLoading(true);
    try {
      const data = await facilityService.getAll({
        page: targetPage,
        page_size: pageSize,
        ...getFacilityFilters(),
      });
      setFacilities(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || targetPage);
    } catch (err) {
      setLoadError(err?.detail || 'Failed to load facilities');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, getFacilityFilters]);

  useEffect(() => {
    loadFacilities();
  }, [loadFacilities]);

  useEffect(() => {
    const loadHmisOptions = async () => {
      try {
        const data = await vendorService.getDropdownConfig({ category: 'facility_hmis', status: 'active' });
        setHmisOptions(data.items || []);
      } catch (_) {
        setHmisOptions([]);
      }
    };

    loadHmisOptions();
  }, []);

  const onLookup = async () => {
    setLookupMessage('');
    setLookupError('');

    if (!form.id.trim()) {
      setLookupError('Enter a facility ID first');
      return;
    }

    setLookupLoading(true);
    try {
      const results = await facilityService.searchFacilityRegistry(form.id.trim(), 'fr-code');
      
      if (!results || results.length === 0) {
        setLookupError('Facility not found in FR registry. You can proceed with manual data entry.');
        setManualEntryEnabled(true);
        setLookupSucceeded(false);
        return;
      }

      const frData = results[0];
      const address = frData.address || {};
      
      // Check for NESP contract
      const insuranceContracts = frData.insuranceContracts || [];
      let hasNespContract = false;
      
      for (const contract of insuranceContracts) {
        if (contract.insurer === 'SHA') {
          const contractTypes = contract.contractTypes || [];
          for (const contractType of contractTypes) {
            if (contractType.contractType === 'NESP' && contractType.status === 'ACTIVE') {
              hasNespContract = true;
              break;
            }
          }
        }
        if (hasNespContract) break;
      }

      // Extract KEPH level number
      const kephLevelMatch = String(frData.kephLevel || '').match(/\d+/);
      const kephLevel = kephLevelMatch ? parseInt(kephLevelMatch[0], 10) : 3;

      // Normalize ownership
      let ownership = 'public';
      const ownershipValue = (frData.facilityOwnership || '').toLowerCase();
      if (ownershipValue.includes('private')) ownership = 'private';
      else if (ownershipValue.includes('faith')) ownership = 'faith_based';
      else if (ownershipValue.includes('ngo')) ownership = 'ngo';
      else if (ownershipValue.includes('parastatal')) ownership = 'parastatal';
      else if (ownershipValue.includes('military')) ownership = 'military';

      // Normalize facility license status to facility status
      let facilityStatus = 'inactive';
      const licenseStatus = (frData.facilityLicenseStatus || '').toUpperCase();
      if (licenseStatus === 'LICENSED') facilityStatus = 'active';
      else if (licenseStatus.includes('NOTLICENSED') || licenseStatus.includes('UNLICENSED')) facilityStatus = 'inactive';
      else if (licenseStatus === 'SUSPENDED') facilityStatus = 'suspended';

      // Extract SHA contract status from SHAOperationStatus
      let shaContractStatus = 'pending';
      const shaOperationStatus = frData.SHAOperationStatus || frData.shaOperationStatus;
      if (shaOperationStatus && shaOperationStatus.operationalStatus) {
        const opStatus = shaOperationStatus.operationalStatus.toUpperCase();
        if (opStatus === 'ACTIVE') shaContractStatus = 'active';
        else if (opStatus === 'INACTIVE') shaContractStatus = 'inactive';
        else if (opStatus === 'EXPIRED') shaContractStatus = 'expired';
        else if (opStatus.includes('PENDING') || opStatus.includes('INPROGRESS')) shaContractStatus = 'pending';
      }

      setForm((prev) => ({
        ...prev,
        id: frData.frCode || frData.fidCode || frData.registrationNumber || prev.id,
        name: frData.officialName || frData.facilityType || '',
        keph_level: kephLevel,
        ownership,
        county: (address.county || 'Unknown').trim(),
        subcounty: address.subCounty || address.subcounty || 'Unknown',
        ward: address.ward || address.constituency || 'Unknown',
        hmis: '',  // Must be selected manually
        source: 'api',
        status: facilityStatus,
        fr_data: frData,
        has_nesp_contract: hasNespContract,
        sha_contract_status: shaContractStatus,
      }));
      
      setManualEntryEnabled(true);
      setLookupSucceeded(true);
      
      const nespStatus = hasNespContract 
        ? '✅ Active NESP contract found' 
        : '⚠️ No active NESP contract - will be added during onboarding';
      setLookupMessage(`Lookup successful for ${frData.frCode}. ${nespStatus}. Review details and click "Onboard Facility".`);
    } catch (err) {
      setManualEntryEnabled(true);
      setLookupSucceeded(false);
      setLookupError(`${err?.response?.data?.detail || err?.message || 'Lookup failed'}. You can proceed with manual data entry.`);
    } finally {
      setLookupLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLookupError('');
    setLookupMessage('');
    setSaving(true);

    try {
      if (lookupSucceeded && form.fr_data) {
        // Use onboard endpoint with FR data
        const result = await facilityService.onboardFacility({
          fr_data: form.fr_data,
          callback_url: form.callback_url,
          username: form.username || null,
          password: form.password || null,
          force_nesp_update: false,
        });
        
        setLookupMessage(result.message || 'Facility onboarded successfully');
      } else {
        // Manual entry - use standard create endpoint
        await facilityService.create({
          ...form,
          id: form.id.trim().toUpperCase(),
          keph_level: Number(form.keph_level),
          source: 'manual_entry',
        });
        setLookupMessage('Facility created successfully');
      }
      
      setForm(initialForm);
      setShowCreateModal(false);
      setManualEntryEnabled(false);
      setLookupSucceeded(false);
      await loadFacilities();
    } catch (err) {
      setLookupError(err?.response?.data?.detail || err?.detail || err?.message || 'Failed to onboard facility');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (facility) => {
    setEditId(facility.id);
    setEditForm({
      id: facility.id || '',
      name: facility.name || '',
      keph_level: facility.keph_level || 3,
      ownership: facility.ownership || 'public',
      county: facility.county || '',
      subcounty: facility.subcounty || '',
      ward: facility.ward || '',
      hmis: facility.hmis || '',
      source: facility.source || 'manual_entry',
      callback_url: facility.callback_url || 'http://localhost:8000/requests/callback',
      username: facility.username || '',
      password: '',
      status: facility.status || 'active',
      sha_contract_status: facility.sha_contract_status || 'pending',
    });
  };

  const cancelEdit = () => {
    setEditId('');
    setEditForm(initialEditForm);
  };

  const onUpdate = async (e) => {
    e.preventDefault();
    if (!editId) return;
    setSaving(true);
    setLookupError('');
    try {
      await facilityService.update(editId, {
        ...editForm,
        keph_level: Number(editForm.keph_level),
      });
      cancelEdit();
      await loadFacilities();
    } catch (err) {
      setLookupError(err?.detail || 'Failed to update facility');
    } finally {
      setSaving(false);
    }
  };

  const onFilter = (e) => {
    e.preventDefault();
    setPage(1);
    loadFacilities(1);
  };

  const escapeCsv = (value) => {
    const text = value == null ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };

  const exportFacilities = async () => {
    setExporting(true);
    setLoadError('');
    try {
      const firstPage = await facilityService.getAll({
        page: 1,
        page_size: 100,
        ...getFacilityFilters(),
      });
      const allItems = [...(firstPage.items || [])];
      const pages = Math.ceil((firstPage.total || 0) / 100);
      for (let nextPage = 2; nextPage <= pages; nextPage += 1) {
        const data = await facilityService.getAll({
          page: nextPage,
          page_size: 100,
          ...getFacilityFilters(),
        });
        allItems.push(...(data.items || []));
      }

      const columns = [
        ['id', 'ID'],
        ['name', 'Name'],
        ['county', 'County'],
        ['subcounty', 'Subcounty'],
        ['ward', 'Ward'],
        ['hmis', 'HMIS'],
        ['source', 'Source'],
        ['status', 'Status'],
        ['sha_contract_status', 'SHA Contract'],
        ['nesp_fr_updated', 'NESP in FR'],
        ['callback_url', 'Callback URL'],
      ];
      const csv = [
        columns.map(([, label]) => escapeCsv(label)).join(','),
        ...allItems.map((facility) => columns.map(([key]) => escapeCsv(facility[key])).join(',')),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facilities-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLoadError(err?.detail || 'Failed to export facilities');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className={styles.page}>
          <div className={styles.pageHeader}>
            <h1>Facilities Onboarding</h1>
            <button type="button" className={styles.addButton} onClick={() => setShowCreateModal(true)}>Add Facility</button>
          </div>
          {showCreateModal ? (
            <div className={styles.modalOverlay}>
              <div className={styles.modal}>
                <div className={styles.modalHeader}>
                  <h2>Add Facility</h2>
                  <button type="button" className={styles.secondary} onClick={() => setShowCreateModal(false)}>Close</button>
                </div>

                <div className={styles.panel}>
                  <p>Enter a Facility ID (FR Code), run lookup to fetch data from FR registry, then click "Onboard Facility".</p>

                  <div className={styles.lookupRow}>
                    <input
                      placeholder="FID-xx-xxxxxx-x"
                      value={form.id}
                      onChange={(e) => setForm((prev) => ({ ...prev, id: e.target.value.toUpperCase() }))}
                    />
                    <button type="button" onClick={onLookup} disabled={lookupLoading}>
                      {lookupLoading ? 'Looking up...' : 'Lookup Facility'}
                    </button>
                  </div>

                  {lookupMessage ? <div className={styles.success}>{lookupMessage}</div> : null}
                  {lookupError ? <div className={styles.warning}>{lookupError}</div> : null}
                  
                  {lookupSucceeded && form.fr_data && (
                    <div className={styles.nespStatus}>
                      <strong>NESP Contract Status:</strong>{' '}
                      {form.has_nesp_contract ? (
                        <span className={styles.nespActive}>✅ Active NESP contract found</span>
                      ) : (
                        <span className={styles.nespPending}>⚠️ No active NESP contract - will be added during onboarding</span>
                      )}
                    </div>
                  )}
                </div>

                <div className={styles.panel}>
                  <h2>Facility Details</h2>
                  <form onSubmit={onSubmit} className={styles.grid}>
                    <input
                      required
                      placeholder="Facility_ID"
                      value={form.id}
                      disabled
                      onChange={(e) => setForm((prev) => ({ ...prev, id: e.target.value }))}
                    />
                    <input
                      required
                      placeholder="Facility Name"
                      value={form.name}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                    <input
                      required
                      type="number"
                      min={1}
                      max={6}
                      placeholder="KEPH Level"
                      value={form.keph_level}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, keph_level: e.target.value }))}
                    />

                    <select
                      value={form.ownership}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, ownership: e.target.value }))}
                    >
                      <option value="public">public</option>
                      <option value="private">private</option>
                      <option value="faith_based">faith_based</option>
                      <option value="ngo">ngo</option>
                      <option value="parastatal">parastatal</option>
                      <option value="military">military</option>
                    </select>

                    <input
                      required
                      placeholder="County"
                      value={form.county}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, county: e.target.value }))}
                    />
                    <input
                      required
                      placeholder="Subcounty"
                      value={form.subcounty}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, subcounty: e.target.value }))}
                    />
                    <input
                      required
                      placeholder="Ward"
                      value={form.ward}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, ward: e.target.value }))}
                    />
                    <select
                      required
                      value={form.hmis}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, hmis: e.target.value }))}
                    >
                      <option value="">Select HMIS in use</option>
                      {hmisOptions.map((option) => (
                        <option key={option.id} value={option.name}>{option.name}</option>
                      ))}
                    </select>
                    <input
                      required
                      type="url"
                      placeholder="Callback URL"
                      value={form.callback_url}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, callback_url: e.target.value }))}
                    />
                    <input
                      placeholder="Username"
                      value={form.username}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                    />
                    <input
                      type="password"
                      placeholder="Password"
                      value={form.password}
                      disabled={!manualEntryEnabled}
                      onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                    />
                    <div>
                      <label>Operational Status:</label>
                      <select
                        value={form.status}
                        disabled={!manualEntryEnabled}
                        onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="active">active</option>
                        <option value="inactive">inactive</option>
                        <option value="suspended">suspended</option>
                      </select>
                    </div>
                    <div>
                      <label>SHA Contract Status:</label>
                      <select
                        value={form.sha_contract_status}
                        disabled={!manualEntryEnabled}
                        onChange={(e) => setForm((prev) => ({ ...prev, sha_contract_status: e.target.value }))}
                      >
                        <option value="active">active</option>
                        <option value="pending">pending</option>
                        <option value="inactive">inactive</option>
                        <option value="expired">expired</option>
                      </select>
                    </div>

                    <div className={styles.inlineActions}>
                      <button type="submit" disabled={saving || !manualEntryEnabled}>
                        {saving ? 'Saving...' : 'Onboard Facility'}
                      </button>
                      <button
                        type="button"
                        className={styles.secondary}
                        onClick={() => {
                          setManualEntryEnabled(true);
                          setLookupSucceeded(false);
                          setLookupError('');
                          setLookupMessage('Manual entry mode enabled. Complete all fields and submit.');
                        }}
                      >
                        Enter Manually
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          ) : null}
      <div className={styles.panel}>
        <h4>Filters</h4>
        <form onSubmit={onFilter} className={styles.filtersRow}>
          <input
            placeholder="Search by facility name"
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value);
              setPage(1);
            }}
          />
          <input
            placeholder="County"
            value={countyFilter}
            onChange={(e) => {
              setCountyFilter(e.target.value);
              setPage(1);
            }}
          />
          <select value={hmisFilter} onChange={(e) => {
            setHmisFilter(e.target.value);
            setPage(1);
          }}>
            <option value="">All HMIS</option>
            {hmisOptions.map((option) => (
              <option key={option.id} value={option.name}>{option.name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}>
            <option value="">All status</option>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
            <option value="suspended">suspended</option>
          </select>
          <select value={pageSize} onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}>
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
          <button type="submit">Apply</button>
          <button type="button" className={styles.secondary} onClick={exportFacilities} disabled={exporting}>
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </form>
      </div>

      <div className={styles.panel}>
        <div className={styles.tableHeader}>
          <h2>Onboarded Facilities</h2>
          <span>{total} total</span>
        </div>
        {loadError ? <div className={styles.error}>{loadError}</div> : null}
        {loading ? <p>Loading facilities...</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>Name</th>
                <th>County</th>
                <th>HMIS</th>
                <th>Source</th>
                <th>Status</th>
                <th>SHA Contract</th>
                <th>NESP in FR</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((facility, index) => (
                <tr key={facility.id}>
                  <td>{(page - 1) * pageSize + index + 1}</td>
                  <td>{facility.id}</td>
                  <td>{facility.name}</td>
                  <td>{facility.county}</td>
                  <td>{facility.hmis}</td>
                  <td>{facility.source === 'api' ? 'API' : 'Manual Entry'}</td>
                  <td>{facility.status}</td>
                  <td>{facility.sha_contract_status}</td>
                  <td title={facility.nesp_fr_last_error || undefined}>
                    {facility.nesp_fr_updated ? 'Updated' : facility.nesp_fr_last_error ? 'Failed' : 'Not updated'}
                  </td>
                  <td className={styles.inlineActions}>
                    {!isVendorUser ? (
                      <Link className={styles.actionLink} to={`/facilities/${facility.id}`}>View</Link>
                    ) : null}
                    <button type="button" onClick={() => startEdit(facility)}>Edit</button>
                  </td>
                </tr>
              ))}
              {facilities.length === 0 ? (
                <tr>
                  <td colSpan={10}>No facilities onboarded yet.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className={styles.pagination}>
          <button type="button" className={styles.secondary} disabled={page <= 1 || loading} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" className={styles.secondary} disabled={page >= totalPages || loading} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}>
            Next
          </button>
        </div>
      </div>

      {editId ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Edit Facility</h2>
              <button type="button" className={styles.secondary} onClick={cancelEdit}>Close</button>
            </div>
            <form onSubmit={onUpdate} className={styles.grid}>
              <input required placeholder="Facility_ID" value={editForm.id} disabled />
              <input required placeholder="Facility Name" value={editForm.name} onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))} />
              <input required type="number" min={1} max={6} placeholder="KEPH Level" value={editForm.keph_level} onChange={(e) => setEditForm((prev) => ({ ...prev, keph_level: e.target.value }))} />

              <select value={editForm.ownership} onChange={(e) => setEditForm((prev) => ({ ...prev, ownership: e.target.value }))}>
                <option value="public">public</option>
                <option value="private">private</option>
                <option value="faith_based">faith_based</option>
                <option value="ngo">ngo</option>
                <option value="parastatal">parastatal</option>
                <option value="military">military</option>
              </select>

              <input required placeholder="County" value={editForm.county} onChange={(e) => setEditForm((prev) => ({ ...prev, county: e.target.value }))} />
              <input required placeholder="Subcounty" value={editForm.subcounty} onChange={(e) => setEditForm((prev) => ({ ...prev, subcounty: e.target.value }))} />
              <input required placeholder="Ward" value={editForm.ward} onChange={(e) => setEditForm((prev) => ({ ...prev, ward: e.target.value }))} />

              <select required value={editForm.hmis} onChange={(e) => setEditForm((prev) => ({ ...prev, hmis: e.target.value }))}>
                <option value="">Select HMIS in use</option>
                {hmisOptions.map((option) => (
                  <option key={option.id} value={option.name}>{option.name}</option>
                ))}
              </select>

              <input required type="url" placeholder="Callback URL" value={editForm.callback_url} onChange={(e) => setEditForm((prev) => ({ ...prev, callback_url: e.target.value }))} />
              <input placeholder="Username" value={editForm.username} onChange={(e) => setEditForm((prev) => ({ ...prev, username: e.target.value }))} />
              <input type="password" placeholder="Password" value={editForm.password} onChange={(e) => setEditForm((prev) => ({ ...prev, password: e.target.value }))} />
              <div>
                <label>Operational Status:</label>
                <select value={editForm.status} onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="suspended">suspended</option>
                </select>
              </div>
              <div>
                <label>SHA Contract Status:</label>
                <select value={editForm.sha_contract_status} onChange={(e) => setEditForm((prev) => ({ ...prev, sha_contract_status: e.target.value }))}>
                  <option value="" disabled>Select SHA Contract Status</option>
                  <option value="active">active</option>
                  <option value="pending">pending</option>
                  <option value="inactive">inactive</option>
                  <option value="expired">expired</option>
                </select>
              </div>

              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Update Facility'}</button>
                <button type="button" className={styles.secondary} onClick={cancelEdit}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FacilitiesPage;
